import { useCallback, useRef, useEffect } from 'react';
import { useGuideStore } from '../store/guideStore';
import { useUserStore } from '../store/userStore';
import { usePenpal } from './usePenpal';
import type { Guide } from '../types';

interface UsePublishGuideOptions {
  onError?: (message: string) => void;
  onUpgradeNeeded?: () => void;
}

export const usePublishGuide = (options: UsePublishGuideOptions = {}) => {
  const { guideToPublish, steps, isPublishing, startPublishing } = useGuideStore();
  const { properties } = useUserStore();
  const { publishGuide } = usePenpal();

  const isProCloudPlan = properties?.portal?.billing?.isProPlan ?? false;
  const cloudBilling = properties?.portal?.billing;

  // Use ref to store the latest options to avoid dependency issues
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const handlePublish = useCallback((extraOptions = {}, guide?: Guide) => {
    // Check billing limits
    if (cloudBilling) {
      const { guideCreated, guidesLimit } = cloudBilling;
      if (!isProCloudPlan && guideCreated >= guidesLimit) {
        optionsRef.current.onUpgradeNeeded?.();
        return;
      }
    }

    // Determine which guide to use
    const guideToUse = guide || guideToPublish;
    if (!guideToUse) {
      const errorMessage = 'No guide data available to publish';
      optionsRef.current.onError?.(errorMessage);
      return;
    }

    // For new guides, validate steps
    if (!guideToUse.id && (!steps || steps.length === 0)) {
      const errorMessage = 'No guide data available to publish';
      optionsRef.current.onError?.(errorMessage);
      return;
    }

    // Start publishing process
    startPublishing({
      title: 'Starting...',
      description: '',
      progress: 1,
      extraOptions
    });

    // Call the parent's publishGuide method
    publishGuide({
      // TODO: fix this logic
      existingGuideId: guide ? guide.id : undefined, // undefined for new guides, guide.id for existing
      existingGuideInternalId: guide ? guide.internalId : undefined,
      ...extraOptions
    });
  }, [guideToPublish, steps, cloudBilling, isProCloudPlan, startPublishing, publishGuide]);

  return {
    handlePublish,
    isPublishing
  };
};
